import {
  ChangeDetectionStrategy,
  Component,
  ErrorHandler,
  Input,
} from '@angular/core';
import { Router } from '@angular/router';
import { Auth } from '@freelancer/auth';
import { ProfileViewUser } from '@freelancer/datastore/collections';
import { QuotationFormHelper } from '@freelancer/quotation';
import { ButtonColor } from '@freelancer/ui/button';
import { HeadingType } from '@freelancer/ui/heading';
import { ToastAlertService, ToastAlertType } from '@freelancer/ui/toast-alert';
import { isDefined, toNumber } from '@freelancer/utils';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { QuotationContextTypeApi } from 'api-typings/quotations/quotations';
import { firstValueFrom } from 'rxjs';

@UntilDestroy({ className: 'DirectQuoteButtonComponent' })
@Component({
  selector: 'app-direct-quote-button',
  template: `
    <fl-button
      flTrackingLabel="DirectQuoteButton"
      i18n="Direct quote button"
      display="block"
      [color]="ButtonColor.SECONDARY"
      [busy]="loading"
      [disabled]="loading"
      (click)="handleQuoteClick()"
    >
      Quote
    </fl-button>

    <fl-toast-alert
      [id]="'quotation-create-fail-alert-' + user.id"
      [type]="ToastAlertType.ERROR"
      [closeable]="true"
      [timeout]="5000"
    >
      <fl-heading
        [headingType]="HeadingType.H6"
        i18n="Error message heading"
      >
        Oops, something went wrong
      </fl-heading>
      <p i18n="Error message for quotation failed to create">
        There's a problem with creating a quote. Please try again.
      </p>
      <p i18n="Redirect to support suggestion">
        If the problem persists, please get in touch with
        <fl-link
          flTrackingLabel="LinkToSupportError"
          [link]="'/support'"
        >
          support.
        </fl-link>
      </p>
    </fl-toast-alert>
  `,
  styleUrls: ['./direct-quote-button.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DirectQuoteButtonComponent {
  ButtonColor = ButtonColor;
  ToastAlertType = ToastAlertType;
  HeadingType = HeadingType;

  @Input() user: ProfileViewUser;

  loading = false;

  constructor(
    private auth: Auth,
    private quotationHelper: QuotationFormHelper,
    private router: Router,
    private errorHandler: ErrorHandler,
    private toast: ToastAlertService,
  ) {}

  handleQuoteClick(): void {
    this.loading = true;

    firstValueFrom(this.auth.getUserId().pipe(untilDestroyed(this))).then(
      async authUserId => {
        const currencyId = this.user.primaryCurrency?.id;
        if (!currencyId) {
          this.toast.openById(`quotation-create-fail-alert-${this.user.id}`);
          this.loading = false;
          return;
        }

        const draftQuotation = await this.quotationHelper.getOrCreateDraftQuote(
          {
            creatorId: toNumber(authUserId),
            contextId: this.user.id,
            contextType: QuotationContextTypeApi.USER,
            defaultCurrencyId: currencyId,
          },
        );

        if (!isDefined(draftQuotation)) {
          this.loading = false;
          this.errorHandler.handleError(
            `Could not share quote to client ${this.user.id}.`,
          );

          this.toast.openById(`quotation-create-fail-alert-${this.user.id}`);

          return;
        }

        return this.router.navigate(
          [`quotation/${draftQuotation.quotationUid}/create`],
          {
            queryParams: { from: encodeURIComponent(this.router.url) },
          },
        );
      },
    );
  }
}
